import type { QueryClient } from '@tanstack/react-query';
import type { PlanCode } from '../types/billing.types';

/* ==========================================================================
   Query keys do billing, por tenant

   Ficam fora de useBilling.ts para a conexão de pedidos (useOrdersSignalR)
   revalidar o billing sem carregar os hooks dele.
   ========================================================================== */

export const billingKeys = {
  root: (tenantId: string) => ['billing', tenantId] as const,
  pricing: (tenantId: string) => [...billingKeys.root(tenantId), 'pricing'] as const,
  subscription: (tenantId: string) =>
    [...billingKeys.root(tenantId), 'subscription'] as const,
  invoicesRoot: (tenantId: string) => [...billingKeys.root(tenantId), 'invoices'] as const,
  invoices: (tenantId: string, params: Record<string, unknown>) =>
    [...billingKeys.invoicesRoot(tenantId), params] as const,
  entitlements: (tenantId: string) =>
    [...billingKeys.root(tenantId), 'entitlements'] as const,
  planChangeQuote: (tenantId: string, toPlanCode: PlanCode) =>
    [...billingKeys.root(tenantId), 'plan-change-quote', toPlanCode] as const,
  /** Raiz dos atendimentos (change billing-ai-units): saldo, extrato e compras. */
  aiUnitsRoot: (tenantId: string) => [...billingKeys.root(tenantId), 'ai-units'] as const,
  aiUnits: (tenantId: string) => [...billingKeys.aiUnitsRoot(tenantId), 'summary'] as const,
  aiUnitEntries: (tenantId: string, params: Record<string, unknown>) =>
    [...billingKeys.aiUnitsRoot(tenantId), 'entries', params] as const,
  aiUnitPurchase: (tenantId: string, purchaseId: string) =>
    [...billingKeys.aiUnitsRoot(tenantId), 'purchase', purchaseId] as const,
};

/**
 * O plano mudou (ou pode ter mudado): relê assinatura, faturas, direitos e
 * atendimentos. É o que `billing.plan_changed` faz e o que a reserva por
 * consulta repete quando a mudança deixa de aguardar pagamento. Os
 * atendimentos entram porque o plano decide se a área aparece
 * (`hasAttendantPlan`) e porque o lote proporcional do upgrade nasce no mesmo
 * commit do plano, sem `billing.ai_units_updated` próprio (change
 * billing-ai-units, design decisão 17).
 */
export function revalidatePlanState(queryClient: QueryClient, tenantId: string): void {
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: billingKeys.subscription(tenantId) });
  void queryClient.invalidateQueries({ queryKey: billingKeys.invoicesRoot(tenantId) });
  void queryClient.invalidateQueries({ queryKey: billingKeys.entitlements(tenantId) });
  void queryClient.invalidateQueries({ queryKey: billingKeys.aiUnitsRoot(tenantId) });
}

/**
 * A carteira de atendimentos mudou (`billing.ai_units_updated`, ou a
 * reconexão, que pode ter perdido um): relê saldo, extrato e a compra aberta,
 * e as faturas, porque a mudança de status de uma compra muda a fatura dela.
 */
export function revalidateAiUnits(queryClient: QueryClient, tenantId: string): void {
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: billingKeys.aiUnitsRoot(tenantId) });
  void queryClient.invalidateQueries({ queryKey: billingKeys.invoicesRoot(tenantId) });
}
